import type { JumpEngineInput } from '@shared/engine/jump/core';

/**
 * Jump-mode input source contract. Emits the single-button `down`/`up`
 * lexicon shared with Waves and mania — a `down` while grounded launches a
 * jump, and holding re-launches on each landing (auto-hop). The engine
 * integrates the parabolic arc between edges, so only the edge beats round-trip.
 */
export interface JumpInputSource {
  readonly kind: 'keyboard' | 'replay' | 'pointer';
  /** Drain inputs queued since the last poll; events past `currentBeat` stay queued. */
  poll: (currentBeat: number) => JumpEngineInput[];
  /** Disconnect listeners; idempotent. */
  destroy: () => void;
  /** Whether the button is currently held — drives the HUD + auto-hop visuals. */
  isPressing: () => boolean;
  /** Prepare the source for a new loop: drop stale inputs + re-queue a held press. */
  rehomeForLoopStart: (beat: number) => void;
}

/**
 * Live keyboard + pointer input source for Jump mode. Same key/pointer surface
 * as Waves / Pulse (Space / W / ArrowUp / J / left+right click) so muscle
 * memory transfers between modes. The single button emits `down` on press and
 * `up` on release.
 *
 * `isClockRunning()` gates which presses reach the engine. While it returns
 * false (e.g. the count-in), a press still flips `isPressing()` so the renderer
 * can show its hold / press-swell cue, but nothing is queued — so an eager tap
 * or spam-click during the countdown gives feedback without pre-loading a jump.
 */
export class KeyboardJumpInputSource implements JumpInputSource {
  readonly kind = 'keyboard' as const;
  private queue: JumpEngineInput[] = [];
  private getBeatAtNow: () => number;
  private isClockRunning: () => boolean;
  private heldKeys = new Set<string>();
  private activePointerIds = new Set<number>();
  private active = true;
  private pressing = false;

  constructor(getBeatAtNow: () => number, isClockRunning: () => boolean = () => true) {
    this.getBeatAtNow = getBeatAtNow;
    this.isClockRunning = isClockRunning;
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('pointerdown', this.onPointerDown);
    window.addEventListener('pointerup', this.onPointerUp);
    window.addEventListener('pointercancel', this.onPointerCancel);
    window.addEventListener('contextmenu', this.onContextMenu);
    window.addEventListener('blur', this.onBlur);
  }

  private isJumpKey(code: string, key: string): boolean {
    return (
      code === 'Space' ||
      code === 'KeyW' ||
      code === 'ArrowUp' ||
      code === 'KeyJ' ||
      key === ' '
    );
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    if (e.repeat) {
      return;
    }
    if (e.target instanceof HTMLElement && (
      e.target.matches('input, textarea, select') || e.target.isContentEditable
    )) {
      return;
    }
    if (!this.isJumpKey(e.code, e.key)) {
      return;
    }
    if (this.heldKeys.has(e.code)) {
      return;
    }
    this.heldKeys.add(e.code);
    e.preventDefault();
    this.emitEdge('down');
  };

  private onKeyUp = (e: KeyboardEvent): void => {
    if (!this.active) {
      return;
    }
    if (!this.isJumpKey(e.code, e.key)) {
      return;
    }
    if (!this.heldKeys.delete(e.code)) {
      return;
    }
    if (this.heldKeys.size > 0 || this.activePointerIds.size > 0) {
      return;
    }
    this.emitEdge('up');
  };

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button !== 0 && e.button !== 2) {
      return;
    }
    const target = e.target as HTMLElement | null;
    if (target && target.closest('button, input, label, a')) {
      return;
    }
    if (e.pointerType === 'mouse' && e.button === 2 && !target?.closest('canvas')) {
      return;
    }
    if (this.activePointerIds.has(e.pointerId)) {
      return;
    }
    this.activePointerIds.add(e.pointerId);
    if (this.heldKeys.size > 0 || this.activePointerIds.size > 1) {
      return;
    }
    this.emitEdge('down');
  };

  private onPointerUp = (e: PointerEvent): void => {
    if (!this.active) {
      return;
    }
    if (!this.activePointerIds.delete(e.pointerId)) {
      return;
    }
    if (this.heldKeys.size > 0 || this.activePointerIds.size > 0) {
      return;
    }
    this.emitEdge('up');
  };

  private onPointerCancel = (e: PointerEvent): void => {
    this.activePointerIds.delete(e.pointerId);
    if (this.heldKeys.size === 0 && this.activePointerIds.size === 0 && this.pressing) {
      this.emitEdge('up');
    }
  };

  private onContextMenu = (e: MouseEvent): void => {
    if (!this.active || !this.isClockRunning()) {
      return;
    }
    const target = e.target as HTMLElement | null;
    if (!target?.closest('canvas')) {
      return;
    }
    if (target.closest('button') || target.closest('input') || target.closest('a')) {
      return;
    }
    e.preventDefault();
  };

  private onBlur = (): void => {
    this.heldKeys.clear();
    this.activePointerIds.clear();
    if (this.pressing) {
      this.emitEdge('up');
    }
  };

  private emitEdge(type: 'down' | 'up'): void {
    this.pressing = type === 'down';
    if (!this.isClockRunning()) {
      return;
    }
    const beat = this.getBeatAtNow();
    this.queue.push({ beat, type });
  }

  poll(currentBeat: number): JumpEngineInput[] {
    if (this.queue.length === 0) {
      return [];
    }
    const ready: JumpEngineInput[] = [];
    const deferred: JumpEngineInput[] = [];
    for (const inp of this.queue) {
      if (inp.beat <= currentBeat) {
        ready.push(inp);
      } else {
        deferred.push(inp);
      }
    }
    this.queue = deferred;
    return ready;
  }

  isPressing(): boolean {
    return this.pressing;
  }

  rehomeForLoopStart(beat: number): void {
    this.queue.length = 0;
    if (this.pressing) {
      this.queue.push({ beat, type: 'down' });
    }
  }

  destroy(): void {
    this.active = false;
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('pointerdown', this.onPointerDown);
    window.removeEventListener('pointerup', this.onPointerUp);
    window.removeEventListener('pointercancel', this.onPointerCancel);
    window.removeEventListener('contextmenu', this.onContextMenu);
    window.removeEventListener('blur', this.onBlur);
  }
}

/**
 * Replay-source variant of {@link KeyboardJumpInputSource}. Replays a captured
 * input stream into the engine in beat order — the headless server-side runner
 * uses the same input shape via
 * {@link import('@shared/engine/jump/headlessRunner.js').runJumpReplay}, so a
 * replay run on the client and server emit identical engine traces.
 */
export class ReplayJumpInputSource implements JumpInputSource {
  readonly kind = 'replay' as const;
  private inputs: JumpEngineInput[];
  private cursor = 0;
  private pressing = false;

  constructor(inputs: JumpEngineInput[]) {
    this.inputs = [...inputs].sort((a, b) => a.beat - b.beat);
  }

  poll(currentBeat: number): JumpEngineInput[] {
    const out: JumpEngineInput[] = [];
    while (this.cursor < this.inputs.length && this.inputs[this.cursor].beat <= currentBeat) {
      const inp = this.inputs[this.cursor];
      out.push(inp);
      this.pressing = inp.type === 'down';
      this.cursor += 1;
    }
    return out;
  }

  isPressing(): boolean {
    return this.pressing;
  }

  rehomeForLoopStart(_beat: number): void {
    // Replay inputs are pre-captured; any required `down` at the loop boundary
    // is already part of the replay stream.
  }

  destroy(): void {
    this.inputs = [];
    this.cursor = 0;
  }

  isExhausted(): boolean {
    return this.cursor >= this.inputs.length;
  }
}
